import type { ResumeData, ResumeSettings } from '../types/resume';
import { Mail, Phone, MapPin, Globe, Linkedin } from 'lucide-react';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function SidebarRightTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-slate-800 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <div className="flex">
        <div className="flex-[7] pr-8">
          <header className="border-b-2 border-slate-800 pb-4 mb-6" style={{ textAlign: s.headerAlignment }}>
            {s.photo && <img src={s.photo} alt="" className="w-16 h-16 rounded-full object-cover mx-auto mb-2 border border-slate-800" />}
            <h1 className="font-bold text-slate-900 tracking-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.875 * hScale}rem` }}>{personal.fullName || 'Your Name'}</h1>
            <p className="text-slate-600 mt-1" style={{ fontSize: `${0.875 * bScale}rem` }}>{personal.email}</p>
          </header>

          <main style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
            {personal.summary && (
              <section>
                <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
              </section>
            )}

            {experience.length > 0 && (
              <section>
                <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-4 pb-2 border-b border-slate-200" style={{ fontSize: `${0.875 * hScale}rem` }}>WORK EXPERIENCE</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                  {experience.map((exp) => (
                    <div key={exp.id}>
                      <div className="flex justify-between items-baseline">
                        <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{exp.title}</h3>
                        <span className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>
                          {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                        </span>
                      </div>
                      <p className="text-slate-700 font-medium" style={{ fontSize: `${0.875 * bScale}rem` }}>{exp.company}{exp.location && ` · ${exp.location}`}</p>
                      {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {education.length > 0 && (
              <section>
                <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-4 pb-2 border-b border-slate-200" style={{ fontSize: `${0.875 * hScale}rem` }}>Education</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                  {education.map((edu) => (
                    <div key={edu.id} className="flex justify-between items-baseline">
                      <div>
                        <h3 className="font-semibold text-slate-900" style={{ fontSize: `${0.875 * hScale}rem` }}>{edu.school}</h3>
                        <p className="text-slate-700" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                      </div>
                      <span className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>
                        {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {projects.length > 0 && (
              <section>
                <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-4 pb-2 border-b border-slate-200" style={{ fontSize: `${0.875 * hScale}rem` }}>Projects</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                  {projects.map((proj) => (
                    <div key={proj.id}>
                      <div className="flex justify-between items-baseline">
                        <h3 className="font-semibold text-slate-900" style={{ fontSize: `${0.875 * hScale}rem` }}>{proj.name}</h3>
                        {proj.url && <a href={proj.url} className="text-blue-600 hover:underline" style={{ fontSize: `${0.75 * bScale}rem` }}>Link</a>}
                      </div>
                      <p className="text-slate-600 mt-1" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                      {proj.technologies.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {proj.technologies.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded" style={{ fontSize: `${0.625 * bScale}rem` }}>{t}</span>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </main>
        </div>

        <div className="flex-[3] bg-slate-50 px-6 py-8 border-l border-slate-200">
          <section className="mb-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-3 pb-2 border-b border-slate-300">Contact</h2>
            <div className="space-y-2" style={{ fontSize: `${0.875 * bScale}rem` }}>
              {personal.email && <div className="flex items-center gap-2 text-slate-700"><Mail className="h-3.5 w-3.5 text-slate-400" /><span className="text-xs">{personal.email}</span></div>}
              {personal.phone && <div className="flex items-center gap-2 text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" /><span className="text-xs">{personal.phone}</span></div>}
              {personal.location && <div className="flex items-center gap-2 text-slate-700"><MapPin className="h-3.5 w-3.5 text-slate-400" /><span className="text-xs">{personal.location}</span></div>}
              {personal.linkedin && <div className="flex items-center gap-2 text-slate-700"><Linkedin className="h-3.5 w-3.5 text-slate-400" /><span className="text-xs">{personal.linkedin}</span></div>}
              {personal.website && <div className="flex items-center gap-2 text-slate-700"><Globe className="h-3.5 w-3.5 text-slate-400" /><span className="text-xs">{personal.website}</span></div>}
            </div>
          </section>

          {skills.length > 0 && (
            <section className="mb-6">
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-3 pb-2 border-b border-slate-300">Skills</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.titleGap }}>
                {skills.map((skill) => (
                  <div key={skill.id} className="border-b border-slate-200 pb-1 font-medium text-slate-800" style={{ fontSize: `${0.75 * bScale}rem` }}>
                    {skill.name}
                  </div>
                ))}
              </div>
            </section>
          )}

          {certifications.length > 0 && (
            <section>
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-3 pb-2 border-b border-slate-300">Certifications</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                {certifications.map((cert) => (
                  <div key={cert.id}>
                    <p className="font-medium text-slate-800" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.name}</p>
                    <p className="text-slate-500" style={{ fontSize: `${0.625 * bScale}rem` }}>{cert.issuer}{cert.date && ` · ${cert.date}`}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
